import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import LoadingSpinner from '../../common/LoadingSpinner.jsx';
import { buildApiUrl } from '../../../utils/apiBase.js';
import { assertAuthApiOk } from '../../../utils/authApiErrors.js';
import { validatePassword } from '../../../utils/password.js';
import HubAuthShell from './HubAuthShell.jsx';

/**
 * パスワードリセット確定ページ（8文字以上・英数字記号で新パスワードを設定）
 */
const ResetPasswordConfirmPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) setError('リセットリンクが無効です。');
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('新しいパスワードが一致しません。');
      return;
    }

    const pwdCheck = validatePassword(newPassword);
    if (!pwdCheck.valid) {
      setError(pwdCheck.message || 'パスワードは8文字以上・英数字記号で入力してください。');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(buildApiUrl('/api/auth/confirm-reset-password'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ token, newPassword }),
      });
      const data = await response.json();

      if (!response.ok) {
        assertAuthApiOk(response, data, 'パスワードの変更に失敗しました。');
        throw new Error(data.message || 'パスワードの変更に失敗しました。');
      }

      setSuccess(true);
      setTimeout(() => navigate('/login', { replace: true }), 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <HubAuthShell>
        <div className="hub-auth-header">
          <h1 className="hub-auth-title">リンクが無効です</h1>
          <p className="hub-auth-subtitle">パスワードリセットを再度申請してください。</p>
        </div>
        <Link to="/reset-password" className="hub-btn hub-btn-primary" style={{ width: '100%', textAlign: 'center' }}>
          リセット申請に戻る
        </Link>
      </HubAuthShell>
    );
  }

  if (success) {
    return (
      <HubAuthShell revealDeps={['success']}>
        <div className="hub-auth-header">
          <div className="hub-auth-logo" style={{ background: 'var(--hub-success)' }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
          </div>
          <h1 className="hub-auth-title">パスワードを変更しました</h1>
          <p className="hub-auth-subtitle">ログインページに移動します...</p>
        </div>
        <Link to="/login" className="hub-btn hub-btn-primary" style={{ width: '100%', textAlign: 'center' }}>
          ログインへ
        </Link>
      </HubAuthShell>
    );
  }

  return (
    <HubAuthShell>
      <div className="hub-auth-header">
        <div className="hub-auth-logo">
          <img src="/logo.webp" alt="Synapse" style={{ width: 48, height: 48, objectFit: 'contain' }} />
        </div>
        <h1 className="hub-auth-title">新しいパスワードを設定</h1>
        <p className="hub-auth-subtitle">8文字以上・英数字記号で入力してください</p>
      </div>

      {error && (
        <div className="hub-auth-error">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="9" />
            <line x1="15" y1="9" x2="9" y2="15" />
            <line x1="9" y1="9" x2="15" y2="15" />
          </svg>
          <span>{error}</span>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="hub-form-group">
          <label className="hub-form-label hub-form-required">新しいパスワード</label>
          <input
            type="password"
            className="hub-form-input"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="8文字以上・英数字記号"
            required
            minLength={8}
            autoComplete="new-password"
          />
        </div>
        <div className="hub-form-group">
          <label className="hub-form-label hub-form-required">新しいパスワード（確認）</label>
          <input
            type="password"
            className="hub-form-input"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="もう一度入力"
            required
            minLength={8}
            autoComplete="new-password"
          />
        </div>

        <button
          type="submit"
          className="hub-btn hub-btn-primary"
          style={{ width: '100%' }}
          disabled={loading}
        >
          {loading ? <><LoadingSpinner size="small" /> 変更中...</> : 'パスワードを変更'}
        </button>
      </form>

      <div className="hub-auth-footer">
        <p>
          <Link to="/login">ログインページに戻る</Link>
        </p>
      </div>
    </HubAuthShell>
  );
};

export default ResetPasswordConfirmPage;
